import { contextHelpers } from '@/components/AiCommandPalette';
import Layout from '@/components/Layout';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from 'react';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaPause, FaPlay } from "react-icons/fa6";
import { FiActivity, FiClock, FiCheckCircle, FiXCircle, FiPauseCircle, FiLoader, FiExternalLink } from 'react-icons/fi';
import { GoArrowLeft } from "react-icons/go";
import { MdOutlineMoreVert } from "react-icons/md";
import { useNavigate, useParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, Tooltip as RechartsTooltip, ReferenceLine, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';

import { LiveMonitorRunStream } from '@/components/LiveMonitorRunStream';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Tooltip as TooltipUI,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import colors from 'tailwindcss/colors';

const formatResponseTime = (ms, longFormat = false) => {
    if (!ms && ms !== 0) return 'N/A';
    
    const formatDecimal = (num) => {
        // Convert to fixed first to handle floating point precision
        const fixed = Number(num.toFixed(1));
        // Check if it's effectively a whole number
        return fixed === Math.floor(fixed) ? Math.floor(fixed) : fixed;
    };

    if (ms < 1000) {
        return `${Math.round(ms)}${longFormat ? ' milliseconds' : 'ms'}`;
    } else if (ms < 60000) {
        const seconds = ms / 1000;
        return `${formatDecimal(seconds)}${longFormat ? ' seconds' : 's'}`;
    } else {
        const minutes = ms / 60000;
        return `${formatDecimal(minutes)}${longFormat ? ' minutes' : 'min'}`;
    }
};

const formatTimeAgo = (elapsedMs) => {
    if (!elapsedMs) return 'just now';
    
    if (elapsedMs < 60000) { // Less than 1 minute
        const seconds = Math.floor(elapsedMs / 1000);
        return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    } else if (elapsedMs < 3600000) { // Less than 1 hour
        const minutes = Math.floor(elapsedMs / 60000);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (elapsedMs < 86400000) { // Less than 1 day
        const hours = Math.floor(elapsedMs / 3600000);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
        const days = Math.floor(elapsedMs / 86400000);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
};

const formatTimeRemaining = (ms) => {
    if (ms < 60000) { // Less than 1 minute
        const seconds = Math.ceil(ms / 1000);
        return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } else if (ms < 3600000) { // Less than 1 hour
        const minutes = Math.ceil(ms / 60000);
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
        const hours = Math.ceil(ms / 3600000);
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
};

const formatRelativeDateTime = (timestamp) => {
    if (!timestamp) return null;
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Format time as "7:30 PM"
    const timeStr = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
    });
    
    // Today
    if (date.toDateString() === now.toDateString()) {
        return `Today at ${timeStr}`;
    }
    
    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday at ${timeStr}`;
    }
    
    // Within last week - show day name
    if (diffDays < 7) {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        return `${dayName} at ${timeStr}`;
    }
    
    // Older - show month and day with ordinal
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    
    // Add ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
    const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    return `${month} ${getOrdinal(day)} at ${timeStr}`;
};

// TODO: Move this to a shared component
const MinimalBadge = ({ children, className }) => (
    <div className={cn(
        "rounded-sm border border-violet-200/50 bg-violet-50/50 px-2 py-0.5 text-[10px] font-medium uppercase text-violet-700 dark:border-violet-800/50 dark:bg-violet-950/50 dark:text-violet-300",
        className
    )}>
        {children}
    </div>
);

/**
 * Get the health status of a monitor (aligned with MonitorListCard)
 */
const getHealthStatus = (monitor, isPolling = false) => {
    const isRunning = monitor?.status?.toLowerCase() === 'running' && monitor?.isActive;
    const hasLastRun = monitor?.lastRun !== null;
    const lastRunSuccess = monitor?.lastRun?.success;
    const isCurrentlyRunning = monitor?.lastRun?.isRunning || isPolling;

    if (isCurrentlyRunning) {
        return {
            label: 'Running',
            textColor: 'text-violet-500',
            icon: Loader2,
        };
    }

    if (!isRunning) {
        return {
            label: 'Paused',
            textColor: 'text-amber-500',
            icon: FiPauseCircle,
        };
    }

    if (!hasLastRun) {
        return {
            label: 'Pending',
            textColor: 'text-muted-foreground',
            icon: FiLoader,
        };
    }

    if (lastRunSuccess) {
        return {
            label: 'Healthy',
            textColor: 'text-emerald-500 dark:text-emerald-400',
            icon: FiCheckCircle,
        };
    }

    return {
        label: 'Failing',
        textColor: 'text-red-500',
        icon: FiXCircle,
    };
};

const CustomYAxisTick = ({ x, y, payload }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={4}
                textAnchor="end"
                fill="hsl(var(--muted-foreground))"
                fontSize={12}
            >
                {`${payload.value} ms`}
            </text>
        </g>
    );
};

const TooltipItem = ({ label, value }) => (
    <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{label}:</span>
        <span className="font-medium ml-2">{value}</span>
    </div>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-background p-4 rounded-lg shadow-lg border border-border">
                <p className="font-bold text-lg mb-2 text-primary">{new Date(data.timestamp).toLocaleString()}</p>
                {data.runCount ? (
                    <div className="space-y-2">
                        <TooltipItem label="Avg Response Time" value={formatResponseTime(data.responseTime)} />
                        <TooltipItem label="Success Rate" value={`${data.successRate.toFixed(2)}%`} />
                        <TooltipItem label="Run Count" value={data.runCount} />
                    </div>
                ) : (
                    <div className="space-y-2">
                        <TooltipItem label="Response Time" value={formatResponseTime(data.responseTime)} />
                        <TooltipItem label="Status" value={data.status} />
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const InsightGrid = ({ insights }) => {
    const daysToShow = 14;
    const responseLevels = [
        'bg-violet-500/20',  // No data/slowest
        'bg-violet-500/60',  // Slow
        'bg-violet-500/80',  // Medium
        'bg-violet-500'   // Fast
    ];
    
    const getDateKey = (daysAgo) => {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        return date.toISOString().split('T')[0];
    };

    const getPerformanceLevel = (avgResponseTime) => {
        if (!avgResponseTime) return 0;
        if (avgResponseTime > 1000) return 1;
        if (avgResponseTime > 500) return 2;
        return 3;
    };

    const insightMap = insights?.reduce((acc, day) => {
        acc[day.date] = day;
        return acc;
    }, {}) || {};

    const formatTooltipData = (dateKey, dayData) => {
        return (
            <div className="space-y-1.5">
                <p className="font-semibold">{dateKey}</p>
                <div className="text-sm space-y-1">
                    <p>Response Time: {formatResponseTime(dayData.avgResponseTime)}</p>
                    <p>Incidents: {dayData.incidents || 0}</p>
                    <p>Runs: {dayData.totalRuns || 0}</p>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-2">
            <div className="flex gap-1.5">
                <TooltipProvider>
                    {Array(daysToShow).fill().map((_, dayIndex) => {
                        const daysAgo = daysToShow - dayIndex - 1;
                        const dateKey = getDateKey(daysAgo);
                        const dayData = insightMap[dateKey] || { avgResponseTime: 0, incidents: 0, totalRuns: 0 };
                        const level = getPerformanceLevel(dayData.avgResponseTime);
                        
                        return (
                            <TooltipUI key={dayIndex} delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <div
                                        className={`
                                            w-6 h-6 rounded-sm relative
                                            ${responseLevels[level]}
                                            transition-colors
                                            hover:ring-2 hover:ring-violet-200
                                            cursor-pointer
                                        `}
                                    >
                                        {dayData.incidents > 0 && (
                                            <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
                                        )}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {formatTooltipData(dateKey, dayData)}
                                </TooltipContent>
                            </TooltipUI>
                        );
                    })}
                </TooltipProvider>
            </div>
        </div>
    );
};

const ResponseTimeChart = ({ data }) => {
    const [activeDataPoint, setActiveDataPoint] = useState(null);
    const navigate = useNavigate();
    const { id } = useParams();
    
    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">No data available</p>
            </div>
        );
    }

    // Function to determine if a data point represents a failed status
    const isFailedStatus = (dataPoint) => {
        // Check for "Failure" status (with capital F)
        return dataPoint.status === "Failure";
    };

    // Get failed data points
    const failedDataPoints = data.filter(isFailedStatus);
    console.log("Failed data points:", failedDataPoints);

    // Custom active dot component that changes color based on status
    const CustomActiveDot = (props) => {
        const { cx, cy, payload } = props;
        const isFailed = isFailedStatus(payload);
        
        return (
            <circle 
                cx={cx} 
                cy={cy} 
                r={4} 
                fill={isFailed ? colors.red[500] : colors.violet[500]} 
                stroke={isFailed ? colors.red[500] : colors.violet[500]} 
                strokeWidth={2}
            />
        );
    };

    // Handle mouse move to update active data point
    const handleMouseMove = (e) => {
        if (e && e.activePayload && e.activePayload.length > 0) {
            setActiveDataPoint(e.activePayload[0].payload);
        } else {
            setActiveDataPoint(null);
        }
    };

    // Handle click on the chart
    const handleClick = (e) => {
        if (activeDataPoint && activeDataPoint.runId) {
            navigate(`/monitors/${id}/runs/${activeDataPoint.runId}`);
        }
    };

    // Handle key press (Enter key)
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && activeDataPoint && activeDataPoint.runId) {
            navigate(`/monitors/${id}/runs/${activeDataPoint.runId}`);
        }
    };

    // Determine cursor color based on active data point
    const cursorColor = activeDataPoint && isFailedStatus(activeDataPoint) 
        ? colors.red[500] 
        : "hsl(var(--border))";

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart 
                data={data}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
                onKeyPress={handleKeyPress}
                tabIndex={0}
                role="button"
                aria-label="Response time chart - click on a data point to view details"
            >
                <CartesianGrid 
                    vertical={false} 
                    horizontal={true}
                    stroke="hsl(var(--border))"
                    strokeWidth={1}
                />
                <XAxis
                    dataKey="timestamp"
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                />
                <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={<CustomYAxisTick />}
                />
                <RechartsTooltip 
                    content={<CustomTooltip />} 
                    cursor={{ 
                        stroke: cursorColor, 
                        strokeWidth: 2,
                        strokeDasharray: "0" // Solid line
                    }}
                />
                <Line 
                    type="monotone" 
                    dataKey="responseTime" 
                    stroke={colors.violet[500]}
                    strokeWidth={2} 
                    dot={false}
                    activeDot={<CustomActiveDot />}
                />
                {failedDataPoints.map((dataPoint, index) => (
                    <ReferenceLine
                        key={`ref-${index}`}
                        x={dataPoint.timestamp}
                        stroke={colors.red[500]}
                        strokeWidth={2}
                        strokeDasharray="3 3"
                    />
                ))}
                <Scatter
                    data={failedDataPoints}
                    dataKey="responseTime"
                    fill={colors.red[500]}
                    stroke={colors.red[500]}
                    strokeWidth={2}
                    shape="circle"
                    r={3}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

const MonitorDetails = () => {
    const [monitor, setMonitor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [newMonitorName, setNewMonitorName] = useState();
    const [liveRunId, setLiveRunId] = useState(null); // For live streaming
    const [isRunInitializing, setIsRunInitializing] = useState(false); // Loading state for run initialization
    const { id, runId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [pauseToggleLoading, setPauseToggleLoading] = useState(false);
    const { axiosInstance } = useAuth();
    const { setCurrentAiPageContext } = useAiPageContext();
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = useRef(null);
    const lastRunDataRef = useRef(null);
    
    // Countdown state (aligned with MonitorListCard logic)
    const [nextRunTargetTime, setNextRunTargetTime] = useState(null);
    const [countdown, setCountdown] = useState(null);
    const [lastRunElapsedMs, setLastRunElapsedMs] = useState(0);

    const fetchMonitorDetails = async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            const response = await axiosInstance.get(`/monitors/${id}`);
            console.log("Monitor data:", response.data);
            console.log("Chart data:", response.data.chartData);
            const monitorData = response.data;
            setMonitor(monitorData);
            setNewMonitorName(monitorData.name);

            // Initialize lastRunDataRef
            if (monitorData.lastRun) {
                lastRunDataRef.current = {
                    runId: monitorData.lastRun.runId,
                    endTime: monitorData.lastRun.endTime
                };
                
                // Start polling if monitor is currently running
                if (monitorData.lastRun.isRunning || !monitorData.lastRun.endTime) {
                    startPolling();
                }
            }
            
            // Set page context for AI Command Palette
            const monitorContext = contextHelpers.createMonitorContext({
                id,
                name: monitorData.name,
                url: monitorData.url,
                lastRun: monitorData.lastRun,
                isActive: monitorData.isActive,
                actionType: monitorData.actionType,
                stats: monitorData.stats
            });
            setCurrentAiPageContext(monitorContext);
        } catch (error) {
            console.error("Error fetching monitor details:", error);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // Stop polling
    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setIsPolling(false);
        }
    }, []);

    // Poll for updates
    const pollMonitor = useCallback(async () => {
        try {
            const response = await axiosInstance.get(`/monitors/${id}/poll`);
            const pollData = response.data;
            
            const lastKnownData = lastRunDataRef.current;
            
            // Check if run data changed
            const runIdChanged = lastKnownData?.runId !== pollData.lastRun?.runId;
            const endTimeChanged = lastKnownData?.endTime !== pollData.lastRun?.endTime;
            
            if (runIdChanged || endTimeChanged) {
                // Update monitor state with new run data
                setMonitor(prev => ({
                    ...prev,
                    lastRun: {
                        ...prev.lastRun,
                        runId: pollData.lastRun?.runId,
                        success: pollData.lastRun?.success,
                        startTime: pollData.lastRun?.startTime,
                        startTimeTimestamp: pollData.lastRun?.startTimeTimestamp,
                        endTime: pollData.lastRun?.endTime,
                        endTimeTimestamp: pollData.lastRun?.endTimeTimestamp,
                        isRunning: pollData.lastRun?.isRunning,
                        result: prev.lastRun?.result, // Keep existing result data
                        note: pollData.lastRun?.note
                    },
                    nextRun: pollData.nextRun
                }));
                
                // Update tracking ref
                lastRunDataRef.current = {
                    runId: pollData.lastRun?.runId,
                    endTime: pollData.lastRun?.endTime
                };
            }
            
            // Stop polling when run completes
            if (pollData.lastRun?.endTime) {
                stopPolling();
                // Do a silent background refresh to get updated stats, charts, etc.
                fetchMonitorDetails(false);
            }
        } catch (error) {
            console.error('Error polling monitor:', error);
        }
    }, [axiosInstance, id, stopPolling]);

    // Start polling
    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) return; // Already polling
        
        setIsPolling(true);
        pollMonitor(); // Poll immediately
        pollingIntervalRef.current = setInterval(pollMonitor, 3000); // Then every 3s
    }, [pollMonitor]);

    useEffect(() => {
        fetchMonitorDetails();
    }, [id]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Handle tab visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !loading) {
                // Background refresh to get latest data without showing skeleton
                fetchMonitorDetails(false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [loading]);

    // Initialize next run target time from server data (aligned with MonitorListCard)
    useEffect(() => {
        if (monitor?.nextRun?.timestamp) {
            setNextRunTargetTime(monitor.nextRun.timestamp);
            setCountdown(monitor.nextRun.msUntilNextRun);
        } else {
            setNextRunTargetTime(null);
            setCountdown(null);
        }
    }, [monitor?.nextRun?.timestamp]);

    // Update countdown every second based on target time (aligned with MonitorListCard)
    useEffect(() => {
        if (!monitor?.isActive || nextRunTargetTime === null) return;
        
        const interval = setInterval(() => {
            const remaining = nextRunTargetTime - Date.now();
            setCountdown(remaining);
            
            // Start polling when countdown expires
            if (remaining <= 0 && !isPolling) {
                startPolling();
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [monitor?.isActive, nextRunTargetTime, isPolling, startPolling]);

    // Update "last run elapsed" time every second
    useEffect(() => {
        if (!monitor?.lastRun?.startTimeTimestamp) {
            setLastRunElapsedMs(0);
            return;
        }
        
        const updateElapsed = () => {
            const elapsed = Date.now() - monitor.lastRun.startTimeTimestamp;
            setLastRunElapsedMs(elapsed);
        };
        
        updateElapsed(); // Initial update
        const interval = setInterval(updateElapsed, 1000);
        
        return () => clearInterval(interval);
    }, [monitor?.lastRun?.startTimeTimestamp]);

    const fadeInVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 }
    };

    if (loading) {
        return <Layout><LoadingState /></Layout>;
    }

    if (!monitor) {
        return <Layout><div>Monitor not found</div></Layout>;
    }

    const handleEdit = () => {
        navigate(`/monitors/${id}/edit`);
    };

    const handleDelete = () => {
        setIsDeleteDialogOpen(true);
        setDeleteConfirmation('');
    };

    const confirmDelete = async () => {
        try {
            await axiosInstance.delete(`/monitors/${id}`);
            navigate('/monitors');
            toast({
                title: "The monitor has been deleted.",
                variant: "default",
            });
        } catch (error) {
            console.error("Error deleting monitor:", error);
            toast({
                title: "Failed to delete the monitor.",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setIsDeleteDialogOpen(false);
        }
    };

    const handleRunOnce = async () => {
        setIsRunInitializing(true); // Show loading immediately
        try {
            const response = await axiosInstance.post(`/monitors/${id}/run`);
            const newRunId = response.data.runId;
            
            // Open live stream dialog
            if (newRunId) {
                setLiveRunId(newRunId);
                setIsRunInitializing(false); // Close initializing dialog as LiveMonitorRunStream takes over
                startPolling();
            }
        } catch (error) {
            console.error("Error running monitor:", error);
            setIsRunInitializing(false); // Hide loading on error
            toast({
                title: "Failed to run monitor",
                description: "An error occurred while trying to run the monitor.",
                variant: "destructive",
            });
        }
    };

    const handleRename = async () => {
        try {
            await axiosInstance.put(`/monitors/${id}/rename`, { name: newMonitorName });
            const updatedMonitor = { ...monitor, name: newMonitorName };
            setMonitor(updatedMonitor);
            
            // Update context when monitor name changes
            const monitorContext = contextHelpers.createMonitorContext({
                id,
                name: newMonitorName,
                url: updatedMonitor.url,
                lastRun: updatedMonitor.lastRun,
                isActive: updatedMonitor.isActive,
                actionType: updatedMonitor.actionType,
                stats: updatedMonitor.stats
            });
            setCurrentAiPageContext(monitorContext);
            
            setIsRenameDialogOpen(false);
            toast({
                title: "Monitor renamed successfully",
                variant: "default",
            });
        } catch (error) {
            console.error("Error renaming monitor:", error);
            toast({
                title: "Failed to rename monitor",
                description: "Please try again later.",
                variant: "destructive",
            });
        }
    };

    const handlePauseToggle = async () => {
        setPauseToggleLoading(true);
        try {
            await axiosInstance.put(`/monitors/${id}/toggle-active`, { isActive: !monitor.isActive });
            const updatedMonitor = { ...monitor, isActive: !monitor.isActive };
            setMonitor(updatedMonitor);
            
            // Update context when monitor status changes
            const monitorContext = contextHelpers.createMonitorContext({
                id,
                name: updatedMonitor.name,
                url: updatedMonitor.url,
                lastRun: updatedMonitor.lastRun,
                isActive: updatedMonitor.isActive,
                actionType: updatedMonitor.actionType,
                stats: updatedMonitor.stats
            });
            setCurrentAiPageContext(monitorContext);
            
            toast({
                title: monitor.isActive ? "Monitor paused" : "Monitor resumed",
                description: monitor.isActive ? "The monitor has been paused" : "The monitor is now active",
            });
        } catch (error) {
            console.error("Error toggling monitor state:", error);
            toast({
                title: "Failed to update monitor state",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setPauseToggleLoading(false);
        }
    };

    const StatSection = ({ title, value, description }) => (
        <div className="flex-1 px-4">
            <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
    );

    const handleRunClick = (run) => {
        navigate(`/monitors/${id}/runs/${run.id}`);
    };

    return (
        <Layout>
            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5 }}
                className="space-y-6"
            >
                <div className="flex justify-between items-center">
                    <div className="flex-col items-center space-y-2">
                        <div className="flex items-center space-x-4">
                            <div 
                                className="flex items-center space-x-4 cursor-pointer text-3xl group"
                                onClick={() => navigate('/monitors')}
                            >
                                <GoArrowLeft className="group-hover:text-violet-500 transition-colors" />
                                <h1 className="-mt-0.5 font-semibold">{monitor.name}</h1>
                            </div>
                            <TooltipProvider>
                                <TooltipUI>
                                    <TooltipTrigger>
                                        <div className="relative mt-0.5">
                                            {(() => {
                                                const isActive = monitor.isActive;
                                                const isFailing = monitor.lastRun && !monitor.lastRun.success;
                                                const dotColor = !isActive ? 'bg-yellow-500' : isFailing ? 'bg-red-500' : 'bg-emerald-400';
                                                
                                                return (
                                                    <>
                                                        <div className={`w-4 h-4 ${dotColor} rounded-full`} />
                                                        {isActive && (
                                                            <div className={`absolute inset-0 w-4 h-4 ${dotColor} rounded-full animate-ping`} />
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {(() => {
                                            if (!monitor.isActive) return 'Monitor is paused';
                                            if (monitor.lastRun && !monitor.lastRun.success) return 'Monitor is active but failing';
                                            return 'Monitor is active and healthy';
                                        })()}
                                    </TooltipContent>
                                </TooltipUI>
                            </TooltipProvider>
                        </div>
                        <div className="flex items-center space-x-2 pl-12">
                            {monitor.url?.formatted && (
                                <TooltipProvider>
                                    <TooltipUI>
                                        <TooltipTrigger asChild>
                                            <div 
                                                onClick={() => window.open(monitor.url.link, '_blank', 'noopener,noreferrer')}
                                                className="group/url text-sm text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 hover:underline transition-colors cursor-pointer flex items-center gap-1.5 w-fit max-w-full"
                                            >
                                                <span className="truncate max-w-md">{monitor.url.formatted}</span>
                                                <FiExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" align="start" className="max-w-md break-all">
                                            <p>{monitor.url.link}</p>
                                        </TooltipContent>
                                    </TooltipUI>
                                </TooltipProvider>
                            )}
                            {monitor.actionType && (
                                <MinimalBadge>
                                    {monitor.actionType.replace('_', ' ')}
                                    {monitor.url.method && ` : ${monitor.url.method}`}
                                </MinimalBadge>
                            )}
                            {monitor.actionType === 'ping' && monitor.settings?.validateSSL && (
                                <MinimalBadge className="border-green-200/50 bg-green-50/50 text-green-700 dark:border-green-800/50 dark:bg-green-950/50 dark:text-green-300">
                                    SSL Validation Enabled
                                </MinimalBadge>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <Button variant="secondary" onClick={handlePauseToggle} disabled={pauseToggleLoading} loading={pauseToggleLoading}>
                            {monitor.isActive ? <FaPause className="mr-2 h-4 w-4" /> : <FaPlay className="mr-2 h-4 w-4" />}
                            {monitor.isActive ? 'Pause' : 'Resume'}
                        </Button>
                        <Button variant="secondary" onClick={handleEdit}>
                            Edit
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="secondary">
                                    <MdOutlineMoreVert className="h-5 w-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={fetchMonitorDetails} className="cursor-pointer">Reload Data</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setIsRenameDialogOpen(true)} className="cursor-pointer">Rename Monitor</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleRunOnce} className="cursor-pointer">Run Once</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleDelete} className="cursor-pointer text-red-600">Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Rename Dialog */}
                <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rename Monitor</DialogTitle>
                        </DialogHeader>
                        <div className="my-4">
                            <Input
                                value={newMonitorName}
                                onChange={(e) => setNewMonitorName(e.target.value)}
                                placeholder="Enter new monitor name"
                            />
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button onClick={handleRename}>
                                Rename
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <motion.div 
                    variants={fadeInVariants}
                    transition={{ delay: 0.2 }}
                    className="space-y-4"
                >
                    {/* Top row with 3 cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                            <CardContent 
                                className={cn(
                                    "p-6 min-h-40 flex flex-col justify-between transition-colors",
                                    monitor.lastRun?.runId && "cursor-pointer hover:bg-accent/50"
                                )}
                                onClick={() => monitor.lastRun?.runId && navigate(`/monitors/${id}/runs/${monitor.lastRun.runId}`)}
                            >
                                <div className="space-y-3">
                                    <h3 className="text-sm font-bold text-primary mb-2">Current Status</h3>
                                    
                                    {/* Status display (aligned with MonitorListCard) */}
                                    {(() => {
                                        const healthStatus = getHealthStatus(monitor, isPolling);
                                        const StatusIcon = healthStatus.icon;
                                        return (
                                            <div className="flex items-center gap-2">
                                                <StatusIcon className={`h-6 w-6 ${healthStatus.textColor} ${healthStatus.label === 'Running' ? 'animate-spin' : ''}`} />
                                                <p className={`text-2xl font-bold ${healthStatus.textColor}`}>
                                                    {healthStatus.label}
                                                </p>
                                            </div>
                                        );
                                    })()}
                                </div>
                                
                                {/* Next check countdown */}
                                {countdown > 0 && monitor.isActive ? (
                                    <p className="text-sm text-muted-foreground">
                                        Next check in {formatTimeRemaining(countdown)}
                                    </p>
                                ) : !monitor.isActive ? (
                                    <p className="text-sm text-muted-foreground">
                                        Monitor is paused
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        No checks scheduled
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent 
                                className={cn(
                                    "p-6 min-h-40 flex flex-col justify-between transition-colors",
                                    monitor.lastRun?.runId && "cursor-pointer hover:bg-accent/50"
                                )}
                                onClick={() => monitor.lastRun?.runId && navigate(`/monitors/${id}/runs/${monitor.lastRun.runId}`)}
                            >
                                <div>
                                    <h3 className="text-sm font-bold text-primary mb-2">Last Check</h3>
                                    {monitor.lastRun ? (
                                        monitor.lastRun.isRunning || isPolling ? (
                                            <p className="text-2xl font-bold text-violet-600">
                                                Check in progress...
                                            </p>
                                        ) : monitor.lastRun.result?.responseTime ? (
                                            <p className={`text-2xl font-bold ${monitor.lastRun.success ? 'text-violet-600' : 'text-red-500'}`}>
                                                {formatResponseTime(monitor.lastRun.result.responseTime, true)}
                                            </p>
                                        ) : (
                                            <p className="text-2xl font-bold text-red-500">Failed</p>
                                        )
                                    ) : (
                                        <p className="text-2xl font-bold text-muted-foreground">No checks yet</p>
                                    )}
                                </div>
                                
                                {/* Last check time ago */}
                                {lastRunElapsedMs > 0 && (
                                    <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                                        <span>{formatRelativeDateTime(monitor.lastRun.startTimeTimestamp)}</span>
                                        <span>•</span>
                                        <span>{formatTimeAgo(lastRunElapsedMs)}</span>
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-6 min-h-40 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-bold text-primary">Bot Insights</h3>
                                        <p className="text-sm font-bold text-primary">
                                            {formatResponseTime(monitor.stats?.last24h?.averageResponseTime)}
                                        </p>
                                    </div>
                                    <InsightGrid insights={monitor.botInsights?.data} />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {monitor.botInsights?.statusSummary || "Analyzing monitor data..."}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Bottom full-width card with 4 sections */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex divide-x">
                                <StatSection 
                                    title="Last 24 Hours" 
                                    value={monitor.stats?.last24h?.uptime || '0%'}
                                    description={`${monitor.stats?.last24h?.successCount || 0} successful checks`}
                                />
                                <StatSection 
                                    title="Last 7 Days" 
                                    value={monitor.stats?.last7d?.uptime || '0%'}
                                    description={`${monitor.stats?.last7d?.successCount || 0} successful checks`}
                                />
                                <StatSection 
                                    title="Last 30 Days" 
                                    value={monitor.stats?.last30d?.uptime || '0%'}
                                    description={`${monitor.stats?.last30d?.successCount || 0} successful checks`}
                                />
                                <StatSection 
                                    title="Last 365 Days" 
                                    value={monitor.stats?.last365d?.uptime || '0%'}
                                    description={`${monitor.stats?.last365d?.successCount || 0} successful checks`}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div
                    variants={fadeInVariants}
                    transition={{ delay: 0.6 }}
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {monitor.actionType?.toLowerCase() === 'ping' ? 'Response Time' : 'Average Page Load Time'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="h-[300px]">
                            <ResponseTimeChart data={monitor.chartData} />
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div
                    variants={fadeInVariants}
                    transition={{ delay: 0.6 }}
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>Recent Checks</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {monitor.recentRuns && monitor.recentRuns.length > 0 ? (    
                                    monitor.recentRuns.map((run) => (
                                        <div 
                                            key={run.id} 
                                            className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-all cursor-pointer group"
                                            onClick={() => handleRunClick(run)}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                {/* Status Indicator */}
                                                {run.success ? (
                                                    <FiCheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-500" />
                                                ) : (
                                                    <FiXCircle className="w-4 h-4 flex-shrink-0 text-red-500" />
                                                )}
                                                
                                                {/* Timestamp and Error */}
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1.5 truncate">
                                                        <span>{formatRelativeDateTime(run.startTimeTimestamp)}</span>
                                                        <span>•</span>
                                                        <span>{formatTimeAgo(Date.now() - run.startTimeTimestamp)}</span>
                                                    </p>
                                                    {run.note && (
                                                        <p className={`text-xs ${run.success ? 'text-primary' : 'text-red-500'} mt-0.5 break-words`}>
                                                            {run.note}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Response Time */}
                                            <div className="flex-shrink-0 font-mono font-semibold text-sm text-foreground">
                                                {formatResponseTime(run.result?.responseTime)}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                        <FiActivity className="w-12 h-12 text-muted-foreground mb-3" />
                                        <p className="text-muted-foreground">No checks recorded yet</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure you want to delete this monitor?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete the monitor
                                and remove all associated data.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="my-6">
                            <p className="text-sm text-muted-foreground mb-3">
                                To confirm, type <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono text-[14px]">{monitor?.name}</span> below:
                            </p>
                            <Input
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e.target.value)}
                                placeholder={monitor?.name}
                                className="mt-2"
                            />
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.toLowerCase() !== monitor?.name.toLowerCase()}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Initializing Dialog - minimal loading state */}
                <Dialog open={isRunInitializing && !liveRunId} onOpenChange={() => setIsRunInitializing(false)}>
                    <DialogContent className="sm:max-w-sm border-purple-500/50 bg-gray-950" hideClose>
                        <div className="flex items-center justify-center space-x-4 py-4">
                            <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
                            <h1 className="text-lg md:text-xl font-semibold animate-pulse">Initializing monitor check view</h1>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Live Stream Dialog - for newly triggered checks */}
                <LiveMonitorRunStream
                    runId={liveRunId}
                    open={!!liveRunId}
                    onOpenChange={(open) => {
                        if (!open) {
                            setLiveRunId(null);
                            setIsRunInitializing(false);
                            // Silent refresh to show the new run without spinner
                            fetchMonitorDetails(false);
                        }
                    }}
                    monitorName={monitor?.name}
                />

                {/* Historical Check Details Dialog - for viewing past checks via URL */}
                <LiveMonitorRunStream
                    runId={runId}
                    open={!!runId}
                    onOpenChange={(open) => {
                        if (!open) {
                            navigate(`/monitors/${id}`);
                        }
                    }}
                    monitorName={monitor?.name}
                />
            </motion.div>
        </Layout>
    );
};

const LoadingState = () => (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
        <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
        <h1 className="text-xl font-semibold">Crunching the data just for you</h1>
    </div>
);

export default MonitorDetails;